// import { LoginResponse as UserLogged } from './../../common/interfaces/login-response.interface';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, Subject, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { UserLogged } from '@fichaje/interfaces/userLogged.interface';
import { FichaService } from '@fichaje/services/ficha.service';
import { ApiRouteService } from '@common/api';
import {
  AuthLoginBootstrap,
  AuthLoginResponse,
  AuthSessionQuery,
  AuthSessionResponse,
  AuthSessionUser,
} from '@auth/interfaces/auth.interface';
import {
  SessionUserFallback,
  SessionUserFallbackSource,
} from '@auth/interfaces/session-user-fallback.interface';
import { IMPERSONATION_STORAGE_KEYS, SHARED_STORAGE_KEYS } from '@common/constants/local-storage-keys.const';

export type AuthErrorKey =
  | 'auth.login.invalidCredentials'
  | 'auth.login.network'
  | 'auth.login.server'
  | 'auth.login.client'
  | 'auth.login.tooManyAttempts'
  | 'auth.login.unknown';

export interface AuthError {
  key: AuthErrorKey;
  message: string;
  detail?: string;
  status?: number;
  attemptsRemaining?: number;
  retryAfterSeconds?: number;
}

export interface LogoutOptions {
  remote?: boolean;
  navigate?: boolean;
}

export type AuthRequestPayload = Record<string, unknown>;

type AuthSessionDataEnvelope = {
  session?: AuthSessionUser;
  menu?: unknown[];
  tareaspendientes?: AuthSessionUser['tareaspendientes'];
  autoaprobado?: AuthSessionUser['autoaprobado'];
  departamentos?: AuthSessionUser['departamentos'];
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Dependency injections

  // authSession y companySelected viven en SHARED_STORAGE_KEYS porque
  // DbCompaniesService también lee/escribe esas mismas claves; el resto son
  // exclusivas de AuthService.
  private readonly storageKeys = {
    token: 'token',
    user: 'user',
    authLoginBootstrap: 'authLoginBootstrap',
    ...SHARED_STORAGE_KEYS,
  } as const;

  private router: Router;
  private http: HttpClient;
  private readonly fichaService = inject(FichaService);
  private readonly apiRoute = inject(ApiRouteService);


  //* SIGNALS
  public isPublic = signal<boolean>(false);

  // User logged signals
  private readonly userLoggedSignal = signal<UserLogged|undefined>(undefined);
  public readonly userLogged$ = this.userLoggedSignal.asReadonly();

  // Auth Guards
  private readonly _isAuthenticated: WritableSignal<boolean> = signal(false);
  public readonly isAuthenticated = this._isAuthenticated.asReadonly();
  public redirectUrl: string | null = null;

  // Emite cada vez que se cierra la sesión local (logout manual, forzado por
  // el interceptor o token inválido al arrancar). ImpersonateService lo
  // escucha para resetear su signal sin crear una dependencia circular.
  private readonly sessionClearedSubject = new Subject<void>();
  public readonly sessionCleared$ = this.sessionClearedSubject.asObservable();

  constructor() {
    this.router = inject(Router);
    this.http = inject(HttpClient);
    this.checkTokenValidity();
  }

  // * =============================================================================================
  // * AUTH REMOTE OPERATIONS
  // * =============================================================================================

  public login(email:string, pass:string):Observable<boolean> {
    this.isPublic.set(true)
    const body = { email:email, password:pass }

    const routePath = this.resolveRoutePath(() => this.apiRoute.authLogin().path, 'authLogin');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth login route'));
    }

    return this.primeCsrfCookie().pipe(
      switchMap(() => this.http.post<AuthLoginResponse>(routePath, body )),
      switchMap((loginResponse) => {
        const accessToken = loginResponse.data?.token?.access_token;
        const companyId = this.resolveCompanyIdFromLogin(loginResponse);

        if (!accessToken) {
          return throwError(() => new Error('[AuthService] Login response missing access_token'));
        }
        if (companyId === undefined || companyId === null) {
          return throwError(() => new Error('[AuthService] Login response missing company_id required for session request'));
        }

        // Bootstrap state required before session call
        this.persistLoginBootstrap(loginResponse, companyId);
        localStorage.setItem(this.storageKeys.token, accessToken);
        this.isPublic.set(false);

        return this.session({
          company_id: companyId,
          // menu_version: loginResponse.data?.menu_version,
          // metadata_version: loginResponse.data?.metadata_version,
        }).pipe(
          map((sessionResponse) => {
            const fallback = this.buildFallbackFromLogin(loginResponse, companyId);
            this.finalizeSession(sessionResponse, accessToken, fallback);
            return true;
          }),
          catchError((err) => {
            this.clearSessionState();
            return throwError(() => err);
          })
        );
      }),
      catchError((err: unknown) => {
        console.error('[AuthService] Login flow failed after auth/login', err);
        return throwError(() => this.normalizeLoginError(err));
      }),
      finalize(() => this.isPublic.set(false))
    )
  }

  public session(query: AuthSessionQuery): Observable<AuthSessionResponse> {
    const routePath = this.resolveRoutePath(() => this.apiRoute.authSession().path, 'authSession');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth session route'));
    }

    let params = new HttpParams().set('company_id', String(query.company_id));
    // Durante la migracion inicial forzamos payload completo para evitar acoplar
    // el cliente al versionado parcial mientras el contrato backend sigue cambiando.
    params = params.set('include', query.include ?? 'menu,tareaspendientes,autoaprobado,departamentos');

    return this.http.get<AuthSessionResponse>(routePath, { params });
  }

  /**
   * Vuelve a pedir la sesión para una empresa distinta a la actual (p.ej.
   * cuando el usuario cambia de empresa desde el selector) y actualiza
   * userLogged$ en caliente, sin necesidad de recargar la página. Reutiliza
   * la misma lógica de mapeo que login(), usando el UserLogged ya guardado
   * como fallback para los campos que /session no traiga.
   */
  public refreshSessionForCompany(companyId: number): Observable<UserLogged> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('[AuthService] No hay token activo para refrescar la sesión'));
    }

    const currentUser = this.getUserLogged();

    return this.session({ company_id: companyId }).pipe(
      map((sessionResponse) => {
        const fallback = this.buildFallbackFromUser(currentUser, companyId);
        return this.finalizeSession(sessionResponse, token, fallback);
      }),
      catchError((err) => {
        console.error('[AuthService] Error al refrescar sesión para nueva empresa', err);
        return throwError(() => err);
      }),
    );
  }

  public me(): Observable<UserLogged> {
    const routePath = this.resolveRoutePath(() => this.apiRoute.authMe().path, 'authMe');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth me route'));
    }

    return this.http.get<UserLogged>(routePath);
  }

  public logoutRemote(): Observable<unknown> {
    const routePath = this.resolveRoutePath(() => this.apiRoute.authLogout().path, 'authLogout');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth logout route'));
    }

    return this.http.post<unknown>(routePath, {});
  }

  public logoutAllRemote(): Observable<unknown> {
    const routePath = this.resolveRoutePath(() => this.apiRoute.authLogoutAll().path, 'authLogoutAll');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth logout-all route'));
    }

    return this.http.post<unknown>(routePath, {});
  }

  public forgotPassword(payload: AuthRequestPayload): Observable<{ data: { uuid: string } }> {
    this.isPublic.set(true);

    const routePath = this.resolveRoutePath(() => this.apiRoute.authForgotPassword().path, 'authForgotPassword');
    if (!routePath) {
      this.isPublic.set(false);
      return throwError(() => new Error('[AuthService] Missing auth forgot-password route'));
    }

    return this.http.post<{ data: { uuid: string } }>(routePath, payload).pipe(
      finalize(() => this.isPublic.set(false))
    );
  }

  public resetPassword(payload: AuthRequestPayload): Observable<{ data: { id: number; uuid: string; name: string; token: string } }> {
    const routePath = this.resolveRoutePath(() => this.apiRoute.authResetPassword().path, 'authResetPassword');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth reset-password route'));
    }

    return this.http.post<{ data: { id: number; uuid: string; name: string; token: string } }>(routePath, payload);
  }

  public changePassword(payload: AuthRequestPayload): Observable<unknown> {
    const routePath = this.resolveRoutePath(() => this.apiRoute.authChangePassword().path, 'authChangePassword');
    if (!routePath) {
      return throwError(() => new Error('[AuthService] Missing auth change-password route'));
    }

    return this.http.post<unknown>(routePath, payload);
  }

  public changePasswordWithResetToken(payload: AuthRequestPayload, resetToken: string): Observable<unknown> {
    localStorage.setItem(this.storageKeys.token, resetToken);

    const routePath = this.resolveRoutePath(() => this.apiRoute.authChangePassword().path, 'authChangePassword');
    if (!routePath) {
      localStorage.removeItem(this.storageKeys.token);
      return throwError(() => new Error('[AuthService] Missing auth change-password route'));
    }

    return this.http.post<unknown>(routePath, payload).pipe(
      finalize(() => localStorage.removeItem(this.storageKeys.token))
    );
  }

private extractAttemptsRemaining(error: HttpErrorResponse): number | undefined {
  const raw = error.headers?.get('X-RateLimit-Remaining');
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    return undefined;
  }
  return parsed + 1;
}

  private extractRetryAfterSeconds(error: HttpErrorResponse): number | undefined {
  const raw = error.headers?.get('Retry-After');
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}

  private normalizeLoginError(error: unknown): AuthError {
  if (!(error instanceof HttpErrorResponse)) {
    const detail = error instanceof Error ? error.message : String(error ?? '');
    return {
      key: 'auth.login.unknown',
      message: $localize`:@@auth.login.unknown:Ocurrió un error inesperado al iniciar sesión.`,
      detail: detail || undefined,
    };
  }

  const backendError = this.extractBackendMessage(error);
  const attemptsRemaining = this.extractAttemptsRemaining(error);

if (error.status === 429) {
  const retryAfterSeconds = this.extractRetryAfterSeconds(error);
  return {
    key: 'auth.login.tooManyAttempts',
    message: $localize`:@@auth.login.tooManyAttempts:Número de intentos superado.`,
    detail: backendError || undefined,
    status: error.status,
    attemptsRemaining: 0,
    retryAfterSeconds: retryAfterSeconds ?? 60, // fallback si el header no llega
  };
}

  if (error.status === 401 || error.status === 402) {
    return {
      key: 'auth.login.invalidCredentials',
      message: $localize`:@@auth.login.invalidCredentials:Credenciales incorrectas. Revisa email y contraseña.`,
      detail: backendError || undefined,
      status: error.status,
      attemptsRemaining,
    };
  }

  if (error.status === 0) {
    return {
      key: 'auth.login.network',
      message: $localize`:@@auth.login.network:No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.`,
      status: error.status,
    };
  }

  if (error.status >= 500) {
    return {
      key: 'auth.login.server',
      message: $localize`:@@auth.login.server:Se produjo un error en el servidor. Inténtalo nuevamente en unos minutos.`,
      status: error.status,
    };
  }

  if (error.status >= 400) {
    return {
      key: 'auth.login.client',
      message: $localize`:@@auth.login.client:No se pudo iniciar sesión. Verifica tus datos e inténtalo otra vez.`,
      detail: backendError || undefined,
      status: error.status,
    };
  }

  return {
    key: 'auth.login.unknown',
    message: $localize`:@@auth.login.unknown:Ocurrió un error inesperado al iniciar sesión.`,
    detail: backendError || undefined,
    status: error.status,
  };
}

  private extractBackendMessage(error: HttpErrorResponse): string {
    if (!error.error) {
      return '';
    }

    if (typeof error.error === 'string') {
      return error.error.trim();
    }

    if (typeof error.error === 'object') {
      const errObj = error.error as { error?: string; message?: string; detail?: string };
      const message = errObj.error || errObj.message || errObj.detail || '';
      return message.trim();
    }

    return '';
  }

  // * =============================================================================================
  // * AUTH LOCAL SESSION STATE
  // * =============================================================================================

  public getToken(): string | undefined {
    const token = localStorage.getItem(this.storageKeys.token);
    return token ?? undefined;
  }

  public getUserLogged(): UserLogged | undefined {
    const userRaw = localStorage.getItem(this.storageKeys.user);
    if (!userRaw) return undefined;

    let ulogged
    try {
      ulogged = JSON.parse(userRaw);
    } catch {
      return undefined;
    }

    // const ulogged:UserLogged = JSON.parse(userRaw); //'169|gz4NsdLGrojHGGEXb7zgPz3zWGy4aJF26B5yRqYye2c03e34'

    if (!ulogged) {
      console.warn('no user logged found')
      return undefined;
    }
    return ulogged;
  }

   public getImgHttp(imageUrl: string): Observable< HttpResponse<Blob> > {
    return this.http.get(imageUrl, { responseType: 'blob', observe: 'response'});
  }

  public logout(options: LogoutOptions = {}): void {
    const { remote = true, navigate = true } = options;
    const finalizeLogout = () => {
      this.clearSessionState();
      if (navigate) {
        this.router.navigateByUrl('auth/login');
      }
    };

    if (!remote || !this.getToken()) {
      finalizeLogout();
      return;
    }

    this.logoutRemote().pipe(
      catchError((error) => {
        console.warn('[AuthService] logoutRemote failed, closing local session anyway', error);
        return of(null);
      }),
      finalize(finalizeLogout),
    ).subscribe();
  }


  // Verifica si el token es válido (por ejemplo, no ha expirado)
  checkTokenValidity(): void {
    const token = this.getToken();
    const userLogged = this.getUserLogged();

    if (token && userLogged) {
      // Aquí puedes agregar lógica para verificar la validez del token
      // (por ejemplo, decodificar el JWT y comprobar su fecha de expiración).
      // Por ahora, asumimos que cualquier token existente es válido.
      this.userLoggedSignal.set(userLogged);
      this._isAuthenticated.set(true);
    } else {
      this.clearSessionState();
    }
  }

  // * =============================================================================================
  // * INTERNAL HELPERS
  // * =============================================================================================

  private resolveRoutePath(routeResolver: () => string, operation: string): string | undefined {
    try {
      return routeResolver();
    } catch {
      console.error(`[AuthService] Failed to resolve route for ${operation}`);
      return undefined;
    }
  }

  /**
   * Llama a `GET /sanctum/csrf-cookie` para que el backend plante las
   * cookies `XSRF-TOKEN` y de sesión ANTES de intentar el login. Sin esta
   * llamada no existe ninguna cookie XSRF que `xsrfInterceptor` pueda copiar
   * al header `X-XSRF-TOKEN`.
   *
   * Si la ruta no se puede resolver o el endpoint falla (p. ej. porque el
   * backend todavía no lo tiene activo), no bloqueamos el login: se
   * continúa con el flujo de Bearer token de siempre.
   */
  private primeCsrfCookie(): Observable<unknown> {
    const csrfCookiePath = this.resolveRoutePath(() => this.apiRoute.authCsrfCookie().path, 'authCsrfCookie');
    if (!csrfCookiePath) {
      return of(null);
    }

    return this.http.get(csrfCookiePath, { withCredentials: true }).pipe(
      catchError((err: unknown) => {
        console.error('[AuthService] Failed to prime CSRF cookie before login', err);
        return of(null);
      })
    );
  }

  private resolveCompanyIdFromLogin(loginResponse: AuthLoginResponse): number | undefined {
    const fromData = this.toNumber(loginResponse.data?.company_id);
    if (fromData !== undefined) {
      return fromData;
    }

    const fromUser = this.toNumber(loginResponse.data?.user?.company_id);
    if (fromUser !== undefined) {
      return fromUser;
    }

    return this.getCompanyIdFromSelectedCompany();
  }

  // Aísla el acceso a la forma cruda de AuthLoginResponse en un único punto,
  // para que el flujo de login() no dependa de la estructura de data.user.
  private buildFallbackFromLogin(loginResponse: AuthLoginResponse, companyId: number): SessionUserFallback {
    const user = loginResponse.data?.user;
    const resolvedCompanyId = loginResponse.data?.company_id ?? user?.company_id ?? companyId;

    return this.buildFallback(user, resolvedCompanyId);
  }

  // Aísla el acceso a la forma cruda de UserLogged (almacenado localmente) en
  // un único punto, para que refreshSessionForCompany() no dependa de su estructura.
  private buildFallbackFromUser(currentUser: UserLogged | undefined, companyId: number): SessionUserFallback {
    return this.buildFallback(currentUser, companyId);
  }

  // Único punto donde se lee la forma compartida (id/name/dni/...) de
  // cualquier fuente de usuario. buildFallbackFromLogin y buildFallbackFromUser
  // solo resuelven de dónde sale esa fuente y el company_id. Al ser
  // SessionUserFallbackSource estructuralmente el mismo shape que
  // SessionUserFallback (sin company_id), no hace falta listar cada campo:
  // el spread ya cubre cualquier campo futuro sin tocar este método.
  private buildFallback(source: SessionUserFallbackSource | undefined, companyId: SessionUserFallback['company_id']): SessionUserFallback {
    return {
      ...source,
      company_id: companyId,
    };
  }

  private getCompanyIdFromSelectedCompany(): number | undefined {
    const rawCompany = localStorage.getItem(this.storageKeys.companySelected);
    if (!rawCompany) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(rawCompany) as { id?: number | string; company_id?: number | string };
      return this.toNumber(parsed.id ?? parsed.company_id);
    } catch {
      return undefined;
    }
  }

  // Paso común a login() y refreshSessionForCompany() tras recibir /session:
  // persistir la respuesta cruda, mapearla al modelo legacy y dejarla como
  // sesión activa. Evita que ambos flujos repitan esta secuencia de 3 pasos.
  private finalizeSession(
    sessionResponse: AuthSessionResponse,
    token: string,
    fallback: SessionUserFallback,
  ): UserLogged {
    this.persistAuthSession(sessionResponse);
    const fullUser = this.mapSessionToLegacyUser(sessionResponse, token, fallback);
    this.setUserLogged(fullUser, token);
    return fullUser;
  }

  private mapSessionToLegacyUser(
    sessionResponse: AuthSessionResponse,
    accessToken: string,
    fallback: SessionUserFallback,
  ): UserLogged {
    const sessionData = this.extractSessionData(sessionResponse);
    const session = this.extractSessionPayload(sessionResponse);
    const workerProfile = session.worker_profile ?? {};
    const roles = session.roles ?? {};
    const workerConfig = session.worker_config ?? {};
    const workerId = this.toNumber(session.id ?? workerProfile.worker_id ?? workerProfile.id ?? fallback.id);
    const companyId = this.toNumber(
      session.company_id
      ?? workerProfile.company_id
      ?? fallback.company_id
    );

    if (workerId === undefined || companyId === undefined) {
      throw new Error('[AuthService] Session payload missing required id/company_id');
    }

    return {
      id: workerId,
      uuid: session.uuid ?? String(workerId),
      name: workerProfile.name ?? session.name ?? fallback.name ?? '',
      dni: workerProfile.dni ?? session.dni ?? fallback.dni ?? '',
      token: accessToken,
      menu: this.coerceMenu(sessionData.menu ?? session.menu),
      firma_requerida: this.pickBoolean(workerProfile.firma_requerida, session.firma_requerida),
      mostrarHorasExtras: this.pickBoolean(workerProfile.horas_extras_jornada, session.mostrarHorasExtras),
      mostrarPermisosRetribuidos: this.pickBoolean(workerProfile.permisos_retribuidos_jornada, session.mostrarPermisosRetribuidos),
      mostrarPausas: this.pickBoolean(workerProfile.pausas, session.mostrarPausas),
      includeLocation: this.pickBoolean(workerProfile.include_location, session.includeLocation),
      trabajoLibre: this.pickBoolean(workerConfig.trabajo_libre, workerProfile.trabajoLibre, session.trabajoLibre),
      fichajeSinAgenda: this.pickBoolean(workerConfig.fichaje_sin_agenda, workerProfile.fichajeSinAgenda, session.fichajeSinAgenda),
      clientesFichaje: this.pickBoolean(workerConfig.clientes_fichaje, session.clientesFichaje),
      ajuste_jornada: this.pickBoolean(workerConfig.ajuste_jornada, session.ajuste_jornada),
      acceso_web: this.pickBoolean(workerConfig.acceso_web, session.acceso_web),
      mapa_requerido_fichar: this.pickBoolean(workerProfile.mapa_requerido_fichar, session.mapa_requerido_fichar),
      coordenadas_requeridas: this.pickBoolean(workerConfig.coordenadas_requeridas, session.coordenadas_requeridas),
      suma_entrada: this.pickBoolean(workerConfig.suma_entrada, session.suma_entrada),
      resta_salida: this.pickBoolean(workerConfig.resta_salida, session.resta_salida),
      saldo_compensado: this.pickBoolean(workerConfig.saldo_compensado, session.saldo_compensado),
      language: workerProfile.language ?? session.language ?? fallback.language ?? 'es',
      company_id: companyId,
      limite_comida: this.toNumber(workerProfile.limite_comida ?? session.limite_comida) ?? 0,
      is_super: this.pickBoolean(roles.is_super, session.is_super, fallback.is_super),
      is_superadmin: this.pickBoolean(roles.is_superadmin, session.is_superadmin, fallback.is_superadmin),
      is_admin: this.pickBoolean(roles.is_admin, session.is_admin, fallback.is_admin),
      is_coordinador: this.pickBoolean(roles.is_coordinador, session.is_coordinador, fallback.is_coordinador),
      is_administracion: this.pickBoolean(roles.is_administracion, session.is_administracion, fallback.is_administracion),
      tareaspendientes: this.coerceLegacyList(sessionData.tareaspendientes ?? session.tareaspendientes),
      departamentos: this.coerceLegacyList(sessionData.departamentos ?? session.departamentos),
      autoaprobado: this.buildAutoaprobado(sessionData.autoaprobado ?? session.autoaprobado, workerId),
    };
  }

  private buildAutoaprobado(
    source: AuthSessionUser['autoaprobado'],
    workerId: number,
  ): UserLogged['autoaprobado'] {
    if (!source) {
      return undefined;
    }

    return {
      section_1: Boolean(source.section_1),
      section_2: Boolean(source.section_2),
      section_3: Boolean(source.section_3),
      section_4: Boolean(source.section_4),
      section_5: Boolean(source.section_5),
      section_6: Boolean(source.section_6),
      section_7: Boolean(source.section_7),
      section_8: Boolean(source.section_8),
      section_9: Boolean(source.section_9),
      section_10: Boolean(source.section_10),
      section_11: Boolean(source.section_11),
      uuid: source.uuid ?? String(workerId),
    };
  }

  private extractSessionData(response: AuthSessionResponse): Partial<AuthSessionDataEnvelope> {
    if (this.hasSessionContainer(response.data)) {
      return response.data;
    }

    return {};
  }

  private extractSessionPayload(response: AuthSessionResponse): AuthSessionUser {
    if (this.hasSessionContainer(response.data) && response.data.session) {
      return response.data.session;
    }

    return response.data as AuthSessionUser;
  }

  private hasSessionContainer(
    data: AuthSessionResponse['data'],
  ): data is AuthSessionDataEnvelope {
    return !!data && typeof data === 'object' && 'session' in data;
  }

  private coerceMenu(menu: unknown[] | undefined): UserLogged['menu'] {
    if (!Array.isArray(menu)) {
      return [];
    }

    return menu as UserLogged['menu'];
  }

  private coerceLegacyList(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      const flattened = value.map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item);
        }

        return JSON.stringify(item);
      });

      return flattened.join(',');
    }

    if (value === undefined || value === null) {
      return '';
    }

    return String(value);
  }


  private toNumber(value: unknown): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  private pickBoolean(...values: Array<boolean | undefined>): boolean {
    for (const value of values) {
      if (typeof value === 'boolean') {
        return value;
      }
    }

    return false;
  }

  private clearSessionState(): void {
    this.fichaService.resetSessionState();
    localStorage.removeItem(this.storageKeys.token);
    localStorage.removeItem(this.storageKeys.user);
    localStorage.removeItem(this.storageKeys.authSession);
    localStorage.removeItem(this.storageKeys.authLoginBootstrap);
    localStorage.removeItem(this.storageKeys.companySelected);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEYS.userId);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEYS.companyId);

    this.userLoggedSignal.set(undefined);
    this._isAuthenticated.set(false);
    this.sessionClearedSubject.next();
  }

  private setUserLogged(user:UserLogged, token?:string):boolean {
    this.fichaService.resetSessionState();
    localStorage.removeItem(this.storageKeys.user);
    this.userLoggedSignal.set(user);
    if(token)
      localStorage.setItem(this.storageKeys.token, token);
    localStorage.setItem(this.storageKeys.user, JSON.stringify(user) );
    this._isAuthenticated.set(true);
    return true
  }

  private persistLoginBootstrap(loginResponse: AuthLoginResponse, companyId: number): void {
    const bootstrap: AuthLoginBootstrap = {
      user: loginResponse.data.user,
      token: loginResponse.data.token,
      company_id: companyId,
      menu_version: loginResponse.data.menu_version,
      metadata_version: loginResponse.data.metadata_version,
    };

    localStorage.setItem(this.storageKeys.authLoginBootstrap, JSON.stringify(bootstrap));
  }

  private persistAuthSession(sessionResponse: AuthSessionResponse): void {
    localStorage.setItem(this.storageKeys.authSession, JSON.stringify(sessionResponse));
  }



}
